import { formatCurrency } from '@/utils/currencyUtils';
import {
  TooltipProps,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Area,
  AreaChart,
  ResponsiveContainer,
} from 'recharts';
import { ChartTooltipContentEcho } from './ChartTooltipEcho';
import { getChartColor } from '@/utils/colorUtils';
import { themeRawValues } from '@/utils/styleUtils';
import { useMemo } from 'react';
import { NetworkEstimatedEarningsByDay } from '@/types';
import { monthNames, uppercaseFirstLetter } from '@/utils';

const EarningsChartEcho = ({ data }: { data: NetworkEstimatedEarningsByDay[] }) => {
  const formatXAxisDate = (date: string) => {
    const dateOnly = date.split('T')[0];
    const [, month, day] = dateOnly.split('-');

    const monthName = uppercaseFirstLetter(monthNames[parseInt(month) - 1].slice(0, 3));
    const dayNum = parseInt(day);

    return `${monthName} ${dayNum}`;
  };

  const preparedData = useMemo(() => {
    // TODO: add filtering for daily/weekly/monthly data
    return data.map((item) => ({
      calculation_date: item.calculation_date,
      color: getChartColor(1),
      total: {
        amount: item.total.amount,
        currency: item.total.currency,
      },
    }));
  }, [data]);

  return (
    <ResponsiveContainer minHeight={325} maxHeight={700} width={'100%'}>
      <AreaChart
        data={preparedData}
        margin={{ top: 16 }}
        role="img"
        aria-label="Estimated earnings chart"
        data-testid="earnings-chart"
      >
        <defs>
          <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
            <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
          </linearGradient>
        </defs>
        <XAxis
          minTickGap={150}
          dataKey="calculation_date"
          tickLine={false}
          tickFormatter={(tick) => formatXAxisDate(tick)}
          axisLine={false}
          tickMargin={10}
          tick={{ fill: '#8B8B8B', fontSize: '0.75rem' }}
          interval={'preserveStartEnd'}
        />
        <YAxis
          // dataKey="total.amount"
          tickLine={false}
          axisLine={false}
          orientation="right"
          tick={({ x, y, payload }) => (
            // custom tick rendering is necessary to align items to the right
            <g transform={`translate(${x},${y})`}>
              <text x={40} y={5} textAnchor="end" fill="#8B8B8B" fontSize="0.75rem">
                {formatCurrency(payload.value, data[0]?.total?.currency, true)}
              </text>
            </g>
          )}
        />

        <Tooltip
          isAnimationActive={true}
          animationDuration={300}
          content={({ active, payload }: TooltipProps<number, string>) =>
            active && payload && payload[0] ? (
              <ChartTooltipContentEcho payload={payload[0].payload} type="date/earnings" />
            ) : null
          }
        />

        <CartesianGrid horizontal vertical={false} strokeWidth="1" stroke="#F0F0F9" />
        <Area
          animationDuration={300}
          strokeWidth={3}
          type="linear"
          dataKey="total.amount"
          stroke={themeRawValues.colors.chartColors.color2}
          fillOpacity={1}
          fill="url(#colorUv)"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default EarningsChartEcho;
